import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { HOME, AUTH } from "@/constants/testIds";
import { Mascot } from "@/components/characters/Characters";
import { CanalesLogo } from "@/components/CanalesLogo";
import { LogIn, LogOut, LayoutDashboard, BookOpen, Gamepad2, ScrollText, ClipboardCheck, Trophy, Map, Newspaper, Library, Users } from "lucide-react";

export const LangToggle = () => {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid={HOME.langToggle}
      className="brutal-btn bg-white text-black px-4 py-2 text-sm"
      aria-label="Toggle language"
    >
      {lang === "es" ? "🇪🇸 ES" : "🇺🇸 EN"}
    </button>
  );
};

const ROLE_LABEL = {
  owner: { es: "Owner", en: "Owner" },
  admin: { es: "Admin", en: "Admin" },
  "admin-lite": { es: "Admin (lectura)", en: "Admin (read-only)" },
  manager: { es: "Manager", en: "Manager" },
  seller: { es: "Vendedor", en: "Seller" },
};

export const UserChip = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  if (!user) return null;
  const role = ROLE_LABEL[user.role]?.[lang] || user.role;
  const store = (user.store || user.location || "").trim();
  const isAdmin = ["admin", "owner", "admin-lite"].includes(user.role);
  const storeLabel = store || (isAdmin ? (lang === "es" ? "Todas las tiendas" : "All stores") : "");
  const initials = (user.name || user.email || "?")
    .trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      data-testid="user-chip"
      title={`${user.name || user.email}${storeLabel ? " · " + storeLabel : ""}`}
      className="hidden sm:inline-flex items-center gap-2 border-2 border-black rounded-full pl-1 pr-3 py-1 bg-white max-w-[240px]"
    >
      <span className="w-7 h-7 shrink-0 rounded-full bg-canales-red text-white font-display font-black text-xs flex items-center justify-center border-2 border-black">
        {initials || "?"}
      </span>
      <span className="leading-tight min-w-0">
        <span className="block font-display font-black text-xs text-black truncate" data-testid="user-chip-name">
          {user.name || user.email}
        </span>
        <span className="block font-body text-[10px] text-neutral-600 truncate" data-testid="user-chip-meta">
          {role}{storeLabel ? ` · ${storeLabel}` : ""}
        </span>
      </span>
    </div>
  );
};

export const Navbar = () => {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const links = [
    { to: "/magazine", label: t("chapters"), Icon: BookOpen },
    { to: "/comics", label: t("comics"), Icon: Newspaper },
    { to: "/biblioteca", label: t("library"), Icon: Library },
    { to: "/games", label: t("games"), Icon: Gamepad2 },
    { to: "/stories", label: t("stories"), Icon: ScrollText },
    { to: "/onboarding", label: t("onboarding"), Icon: Map },
    { to: "/ranking", label: t("ranking"), Icon: Trophy },
    { to: "/exams-info", label: t("exams"), Icon: ClipboardCheck },
  ];

  const handleLogin = () => {
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 bg-canales-yellow border-b-4 border-black">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <CanalesLogo width={140} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map(({ to, label, Icon }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={`px-3 py-2 rounded-full font-display font-bold text-sm flex items-center gap-2 border-2 ${
                  active ? "bg-black text-canales-yellow border-black" : "bg-white text-black border-black/40 hover:border-black"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle />
          {user ? (
            <>
              <UserChip />
              {["admin", "owner", "admin-lite"].includes(user.role) ? (
                <button
                  onClick={() => navigate("/admin")}
                  className="brutal-btn bg-canales-blue text-white px-4 py-2 text-sm flex items-center gap-2"
                  data-testid="nav-admin-btn"
                >
                  <LayoutDashboard className="w-4 h-4" /> {t("admin")}
                </button>
              ) : (
                <button
                  onClick={() => navigate("/manager/team")}
                  className="brutal-btn bg-canales-blue text-white px-4 py-2 text-sm flex items-center gap-2"
                  data-testid="nav-team-btn"
                >
                  <Users className="w-4 h-4" />
                  {user.role === "seller"
                    ? (lang === "es" ? "Mi Tienda" : "My Store")
                    : (lang === "es" ? "Mi Equipo" : "My Team")}
                </button>
              )}
              <button
                onClick={logout}
                data-testid={AUTH.logoutBtn}
                className="brutal-btn bg-white text-black px-3 py-2 text-sm"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={handleLogin}
              data-testid={AUTH.loginBtn}
              className="brutal-btn bg-canales-blue text-white px-4 py-2 text-sm flex items-center gap-2"
            >
              <LogIn className="w-4 h-4" /> {t("login")}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
